import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import {
  getAllAppConfigs,
  bulkUpdateAppConfigs,
  createAppConfig,
  updateAppConfig,
  deleteAppConfig,
  initializeAppConfigs,
  AppConfig,
} from "../../../api/services/api-typed";
import Loader from "../../../components/Loader";
import { RoleGuard } from "../../../components/RBAC";

interface ConfigFormData {
  key: string;
  value: string;
  category: string;
  description: string;
  is_public: boolean;
}

const BOOLEAN_FEATURE_KEYS = [
  {
    key: 'feature.fasih_enabled',
    label: 'Fasih AI Therapist',
    description:
      'When disabled, Fasih is hidden from the user app (nav, home, sessions) and AI APIs are blocked.',
  },
  {
    key: 'feature.maintenance_mode',
    label: 'Maintenance Mode',
    description: 'Enable maintenance mode for the platform.',
  },
  {
    key: 'feature.new_registrations_enabled',
    label: 'New Registrations',
    description: 'Allow new user registrations.',
  },
  {
    key: 'feature.chat_support_enabled',
    label: 'Chat Support',
    description: 'Enable live chat support.',
  },
] as const;

export const AppConfigManagement = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState("all");
  const [editedConfigs, setEditedConfigs] = useState<Record<string, string>>(
    {}
  );
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState<AppConfig | null>(null);
  const [formData, setFormData] = useState<ConfigFormData>({
    key: "",
    value: "",
    category: "general",
    description: "",
    is_public: false,
  });

  // Fetch all configs
  const { data, isLoading, refetch } = useQuery(
    "app-configs",
    () => getAllAppConfigs(),
    {
      onSuccess: (res: any) => {
        // Initialize edited configs with current values
        const initial: Record<string, string> = {};
        res?.data?.configs?.forEach((config: AppConfig) => {
          initial[config.key] = config.value;
        });
        setEditedConfigs(initial);
      },
    }
  );

  // Initialize default configs
  const { mutate: initializeMutate, isLoading: isInitializing } = useMutation(
    initializeAppConfigs,
    {
      onSuccess: () => {
        toast.success("Default configurations initialized successfully");
        refetch();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Failed to initialize configs"
        );
      },
    }
  );

  // Bulk update configs
  const { mutate: bulkUpdateMutate, isLoading: isUpdating } = useMutation(
    bulkUpdateAppConfigs,
    {
      onSuccess: (res: any) => {
        toast.success(
          res?.data?.message || "Configurations updated successfully"
        );
        queryClient.invalidateQueries("app-configs");
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Failed to update configurations"
        );
      },
    }
  );

  // Create config
  const { mutate: createMutate } = useMutation(createAppConfig, {
    onSuccess: () => {
      toast.success("Configuration created successfully");
      setAddDialogOpen(false);
      resetForm();
      refetch();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to create configuration"
      );
    },
  });

  // Update config
  const { mutate: updateMutate } = useMutation(
    ({ key, data }: { key: string; data: any }) => updateAppConfig(key, data),
    {
      onSuccess: () => {
        toast.success("Configuration updated successfully");
        setEditDialogOpen(false);
        setSelectedConfig(null);
        refetch();
      },
      onError: (error: any) => {
        toast.error(
          error?.response?.data?.message || "Failed to update configuration"
        );
      },
    }
  );

  // Delete config
  const { mutate: deleteMutate } = useMutation(deleteAppConfig, {
    onSuccess: () => {
      toast.success("Configuration deleted successfully");
      refetch();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message || "Failed to delete configuration"
      );
    },
  });

  const configs = data?.data?.configs || [];
  const grouped = data?.data?.grouped || {};
  const categories = Object.keys(grouped);

  const handleFeatureToggle = (key: string, enabled: boolean) => {
    handleValueChange(key, enabled ? 'true' : 'false');
  };

  const handleSaveFeatureToggle = (key: string) => {
    const value = editedConfigs[key];
    if (value === undefined) return;
    bulkUpdateMutate([{ key, value }]);
  };

  const getFeatureEnabled = (key: string, defaultValue = true) => {
    const value = editedConfigs[key];
    if (value === undefined) return defaultValue;
    return value.toLowerCase() === 'true';
  };

  const handleValueChange = (key: string, value: string) => {
    setEditedConfigs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSaveAll = () => {
    const changedConfigs = Object.entries(editedConfigs)
      .filter(([key, value]) => {
        const original = configs.find((c: AppConfig) => c.key === key);
        return original && original.value !== value;
      })
      .map(([key, value]) => ({ key, value }));

    if (changedConfigs.length === 0) {
      toast.info("No changes to save");
      return;
    }

    bulkUpdateMutate(changedConfigs);
  };

  const handleAddConfig = () => {
    createMutate(formData);
  };

  const handleEditConfig = () => {
    if (!selectedConfig) return;
    updateMutate({
      key: selectedConfig.key,
      data: {
        value: formData.value,
        category: formData.category,
        description: formData.description,
        is_public: formData.is_public,
      },
    });
  };

  const handleDeleteConfig = (key: string) => {
    if (
      window.confirm(`Are you sure you want to delete configuration '${key}'?`)
    ) {
      deleteMutate(key);
    }
  };

  const openEditDialog = (config: AppConfig) => {
    setSelectedConfig(config);
    setFormData({
      key: config.key,
      value: config.value,
      category: config.category,
      description: config.description || "",
      is_public: config.is_public,
    });
    setEditDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      key: "",
      value: "",
      category: "general",
      description: "",
      is_public: false,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const getFilteredConfigs = () => {
    if (activeTab === "all") return configs;
    if (activeTab === "public")
      return configs.filter((c: AppConfig) => c.is_public);
    if (activeTab === "private")
      return configs.filter((c: AppConfig) => !c.is_public);
    return grouped[activeTab] || [];
  };

  const filteredConfigs = getFilteredConfigs();

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box my={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h3">App Configuration Management</Typography>
        <Box display="flex" gap={2}>
          <Tooltip title="Refresh configurations">
            <IconButton onClick={() => refetch()}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <RoleGuard allowedRoles={["SUPER_ADMIN"]}>
            <Button
              variant="outlined"
              onClick={() => initializeMutate()}
              disabled={isInitializing}
            >
              Initialize Defaults
            </Button>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setAddDialogOpen(true);
              }}
            >
              Add Config
            </Button>
          </RoleGuard>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleSaveAll}
            disabled={isUpdating}
          >
            {isUpdating ? "Saving..." : "Save All Changes"}
          </Button>
        </Box>
      </Box>

      {/* Statistics */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">{configs.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                Total Configurations
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">{categories.length}</Typography>
              <Typography variant="body2" color="text.secondary">
                Categories
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">
                {configs.filter((c: AppConfig) => c.is_public).length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Public Configs
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6">
                {
                  Object.keys(editedConfigs).filter((key) => {
                    const original = configs.find(
                      (c: AppConfig) => c.key === key
                    );
                    return original && original.value !== editedConfigs[key];
                  }).length
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Unsaved Changes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Feature Toggles — quick switches for boolean flags */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" mb={1}>
            User-Facing Feature Toggles
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Control which features are visible to users in the mobile app.
            Changes apply after users refresh or restart the app.
          </Typography>
          {BOOLEAN_FEATURE_KEYS.map(({ key, label, description }) => {
            const configExists = configs.some((c: AppConfig) => c.key === key);
            const isModified =
              configs.find((c: AppConfig) => c.key === key)?.value !==
              editedConfigs[key];
            return (
              <Box
                key={key}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                py={1.5}
                borderBottom="1px solid"
                borderColor="divider"
              >
                <Box flex={1} pr={2}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {label}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {description}
                  </Typography>
                  {!configExists && (
                    <Chip
                      label="Run Initialize Defaults to create"
                      size="small"
                      color="warning"
                      sx={{ mt: 0.5 }}
                    />
                  )}
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  {isModified && (
                    <Chip label="Unsaved" size="small" color="warning" />
                  )}
                  <FormControlLabel
                    control={
                      <Switch
                        checked={getFeatureEnabled(
                          key,
                          key === 'feature.maintenance_mode' ? false : true
                        )}
                        onChange={(e) =>
                          handleFeatureToggle(key, e.target.checked)
                        }
                        disabled={!configExists}
                      />
                    }
                    label=""
                  />
                  {isModified && configExists && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => handleSaveFeatureToggle(key)}
                      disabled={isUpdating}
                    >
                      Save
                    </Button>
                  )}
                </Box>
              </Box>
            );
          })}
        </CardContent>
      </Card>

      {/* Tabs */}
      <Card sx={{ mb: 2 }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => setActiveTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All" value="all" />
          <Tab label="Public" value="public" />
          <Tab label="Private" value="private" />
          {categories.map((category) => (
            <Tab
              key={category}
              label={category.charAt(0).toUpperCase() + category.slice(1)}
              value={category}
            />
          ))}
        </Tabs>
      </Card>

      {/* Configurations List */}
      <Box>
        {activeTab === "all" ? (
          // Group by category when showing all
          categories.map((category) => (
            <Accordion key={category} defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">
                  {category.charAt(0).toUpperCase() + category.slice(1)} (
                  {grouped[category].length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {grouped[category].map((config: AppConfig) => (
                    <Grid item xs={12} md={6} key={config.key}>
                      <ConfigCard
                        config={config}
                        value={editedConfigs[config.key] || config.value}
                        onChange={(value) =>
                          handleValueChange(config.key, value)
                        }
                        onEdit={() => openEditDialog(config)}
                        onDelete={() => handleDeleteConfig(config.key)}
                        onCopy={() => copyToClipboard(config.value)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          // Flat list for filtered views
          <Grid container spacing={2}>
            {filteredConfigs.map((config: AppConfig) => (
              <Grid item xs={12} md={6} key={config.key}>
                <ConfigCard
                  config={config}
                  value={editedConfigs[config.key] || config.value}
                  onChange={(value) => handleValueChange(config.key, value)}
                  onEdit={() => openEditDialog(config)}
                  onDelete={() => handleDeleteConfig(config.key)}
                  onCopy={() => copyToClipboard(config.value)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Add Config Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Configuration</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Key"
              value={formData.key}
              onChange={(e) =>
                setFormData({ ...formData, key: e.target.value })
              }
              sx={{ mb: 2 }}
              helperText="Use dot notation, e.g., contact.whatsapp_number"
            />
            <TextField
              fullWidth
              label="Value"
              value={formData.value}
              onChange={(e) =>
                setFormData({ ...formData, value: e.target.value })
              }
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_public}
                  onChange={(e) =>
                    setFormData({ ...formData, is_public: e.target.checked })
                  }
                />
              }
              label="Public (accessible by apps without auth)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddConfig} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Config Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Configuration</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <TextField
              fullWidth
              label="Key"
              value={formData.key}
              disabled
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Value"
              value={formData.value}
              onChange={(e) =>
                setFormData({ ...formData, value: e.target.value })
              }
              multiline
              rows={3}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Category"
              value={formData.category}
              onChange={(e) =>
                setFormData({ ...formData, category: e.target.value })
              }
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              multiline
              rows={2}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_public}
                  onChange={(e) =>
                    setFormData({ ...formData, is_public: e.target.checked })
                  }
                />
              }
              label="Public (accessible by apps without auth)"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditConfig} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ConfigCard Component
interface ConfigCardProps {
  config: AppConfig;
  value: string;
  onChange: (value: string) => void;
  onEdit: () => void;
  onDelete: () => void;
  onCopy: () => void;
}

const ConfigCard = ({
  config,
  value,
  onChange,
  onEdit,
  onDelete,
  onCopy,
}: ConfigCardProps) => {
  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          mb={1}
        >
          <Box flex={1}>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              {config.key}
            </Typography>
            {config.description && (
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                mb={1}
              >
                {config.description}
              </Typography>
            )}
          </Box>
          <Box display="flex" gap={0.5}>
            {config.is_public && (
              <Chip
                label="Public"
                size="small"
                color="success"
                variant="outlined"
              />
            )}
            <Tooltip title="Copy value">
              <IconButton size="small" onClick={onCopy}>
                <CopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <RoleGuard allowedRoles={["SUPER_ADMIN"]}>
              <Tooltip title="Delete">
                <IconButton size="small" color="error" onClick={onDelete}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </RoleGuard>
          </Box>
        </Box>
        <TextField
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          multiline
          minRows={1}
          maxRows={4}
          size="small"
          variant="outlined"
        />
        <Box display="flex" justifyContent="space-between" mt={1}>
          <Typography variant="caption" color="text.secondary">
            Category: {config.category}
          </Typography>
          {config.value !== value && (
            <Chip label="Modified" size="small" color="warning" />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
